import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Users, Server, Megaphone, ShieldCheck, LogOut, MessageCircle, Menu, X, MessagesSquare, ListChecks,
} from "lucide-react";
import { useState } from "react";

const NAV = [
  { to: "/dashboard", label: "Tableau de bord", icon: LayoutDashboard, id: "nav-dashboard" },
  { to: "/campagnes", label: "Campagnes", icon: Megaphone, id: "nav-campaigns" },
  { to: "/reponses", label: "Réponses", icon: MessagesSquare, id: "nav-inbox" },
  { to: "/contacts", label: "Contacts", icon: Users, id: "nav-contacts" },
  { to: "/listes", label: "Listes", icon: ListChecks, id: "nav-lists" },
  { to: "/instances", label: "Instances", icon: Server, id: "nav-instances" },
  { to: "/admins", label: "Administrateurs", icon: ShieldCheck, id: "nav-admins" },
];

export const Layout = ({ children, title, subtitle, actions }) => {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const sidebar = (
    <div className="flex h-full flex-col bg-ink px-4 py-6">
      <button
        onClick={() => navigate("/dashboard")}
        className="flex items-center gap-3 px-2 mb-8 text-left"
        data-testid="brand-btn"
      >
        <div className="h-9 w-9 rounded-lg bg-emerald-500 grid place-items-center shrink-0">
          <MessageCircle className="h-4.5 w-4.5 text-ink" />
        </div>
        <div>
          <p className="font-display text-base font-semibold text-white leading-none">Diffusion</p>
          <p className="mono text-[10px] text-gray-500 mt-1">CAMPAIGN OPS</p>
        </div>
      </button>

      <nav className="flex flex-col gap-1">
        {NAV.map(({ to, label, icon: Icon, id }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={id}
            onClick={() => setOpen(false)}
            className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
          >
            <Icon className="h-4 w-4 shrink-0" />
            {label}
          </NavLink>
        ))}
      </nav>

      <div className="mt-auto border-t border-inkSoft pt-4">
        <div className="flex items-center gap-3 px-2 pb-3">
          <div className="h-8 w-8 rounded-full bg-emerald-500/20 grid place-items-center mono text-xs text-emerald-300">
            {(user?.email || "?").slice(0, 2).toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="truncate text-xs text-gray-300" data-testid="sidebar-user-email">{user?.email}</p>
            <p className="mono text-[10px] text-emerald-400/80 uppercase">{user?.role}</p>
          </div>
        </div>
        <button onClick={logout} data-testid="logout-btn" className="sidebar-link w-full">
          <LogOut className="h-4 w-4" /> Déconnexion
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <aside className="hidden lg:block fixed inset-y-0 left-0 w-64">{sidebar}</aside>
      {open && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="w-64">{sidebar}</div>
          <div className="flex-1 bg-black/40" onClick={() => setOpen(false)} />
        </div>
      )}

      <main className="lg:ml-64">
        <header className="sticky top-0 z-30 border-b border-gray-200 bg-background/95 backdrop-blur px-5 sm:px-8 py-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                className="lg:hidden rounded-lg border border-gray-200 bg-white p-2"
                onClick={() => setOpen(!open)}
                data-testid="mobile-menu-btn"
              >
                {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
              </button>
              <div>
                <h1 className="text-2xl font-semibold text-gray-900" data-testid="page-title">{title}</h1>
                {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
              </div>
            </div>
            <div className="flex items-center gap-2">{actions}</div>
          </div>
        </header>
        <div className="p-5 sm:p-8 animate-fade-up">{children}</div>
      </main>
    </div>
  );
};

export default Layout;
